"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle,
  faCircleInfo,
  faPhone,
  faPhoneSlash,
  faRoute,
  faUsers,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useToast } from "@/components/ui/ToastContainer";
import { useUser } from "@/contexts/UserContext";
import { SOFTPHONE_DIAL_EVENT, SOFTPHONE_OPEN_EVENT } from "@/components/telephony/events";
import { fetchCallCenterEligibility } from "@/lib/callCenterEligibility";
import "@/styles/customer-call.css";
import "@/styles/softphone.css";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://app.canerozen.com/api";
const SANTRAL_MODULE_ID = 353;

type CallEligibility = {
  canDial: boolean;
  santralActive: boolean;
  reason: string;
  loading: boolean;
};

type OpenCustomerCallOptions = {
  customerName?: string;
  userPhone?: string | null;
};

type ModuleInfo = {
  id: number;
  name: string;
  title: string;
  subtitle: string;
  introTitle: string;
  introText: string;
  features: { title: string; text: string }[];
  videoUrl: string;
  faqs: { question: string; answer: string }[];
};

function getToken() {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("auth_token") || "";
}

export function normalizeTelsamPhone(value?: string | null) {
  let digits = String(value || "").replace(/\D+/g, "");
  if (!digits) return "";
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.startsWith("90") && digits.length === 12) return digits;
  if (digits.startsWith("0") && digits.length === 11) return `9${digits}`;
  if (digits.length === 10) return `90${digits}`;
  if (digits.length > 12) return `90${digits.slice(-10)}`;
  return digits;
}

export function useCustomerCallAction() {
  const { showToast } = useToast();
  const { user } = useUser();
  const [eligibility, setEligibility] = useState<CallEligibility>({
    canDial: false,
    santralActive: false,
    reason: "",
    loading: true,
  });
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [callbackUserPhone, setCallbackUserPhone] = useState("");
  const [callbackCustomerPhone, setCallbackCustomerPhone] = useState("");
  const [callbackCustomerName, setCallbackCustomerName] = useState("");
  const [callbackSaving, setCallbackSaving] = useState(false);
  const [callbackError, setCallbackError] = useState("");
  const [inactiveInfoOpen, setInactiveInfoOpen] = useState(false);
  const [infoRequestSaving, setInfoRequestSaving] = useState(false);
  const [infoRequestMessage, setInfoRequestMessage] = useState("");
  const [moduleInfo, setModuleInfo] = useState<ModuleInfo | null>(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadEligibility() {
      try {
        const data = await fetchCallCenterEligibility(API_BASE, getToken());
        if (cancelled) return;
        setEligibility({
          canDial: Boolean(data?.canDial),
          santralActive: Boolean(data?.santralActive),
          reason: typeof data?.reason === "string" ? data.reason : "",
          loading: false,
        });
      } catch (error) {
        if (cancelled) return;
        setEligibility({
          canDial: false,
          santralActive: false,
          reason: error instanceof Error ? error.message : "Santral yetkisi alınamadı",
          loading: false,
        });
      }
    }
    loadEligibility();
    return () => {
      cancelled = true;
    };
  }, []);

  const openCustomerCall = useCallback((rawCustomerPhone?: string | null, options: OpenCustomerCallOptions = {}) => {
    if (eligibility.loading) {
      showToast("Santral yetkisi kontrol ediliyor.", "info", 1800);
      return;
    }

    const customerPhone = normalizeTelsamPhone(rawCustomerPhone);
    if (!customerPhone || customerPhone.length !== 12) {
      showToast("Aranacak müşteri telefon numarası bulunamadı.", "warning", 3000);
      return;
    }

    if (!eligibility.santralActive) {
      setInactiveInfoOpen(true);
      return;
    }

    if (eligibility.canDial) {
      window.dispatchEvent(new CustomEvent(SOFTPHONE_DIAL_EVENT, { detail: { number: customerPhone } }));
      window.dispatchEvent(new CustomEvent(SOFTPHONE_OPEN_EVENT));
      return;
    }

    setCallbackUserPhone(normalizeTelsamPhone(options.userPhone || user?.phone || ""));
    setCallbackCustomerPhone(customerPhone);
    setCallbackCustomerName(options.customerName || "");
    setCallbackError("");
    setCallbackOpen(true);
  }, [eligibility.canDial, eligibility.loading, eligibility.santralActive, showToast, user?.phone]);

  const openUnavailableInfo = useCallback(async () => {
    setInactiveInfoOpen(true);
    setInfoRequestMessage("");
    try {
      const response = await fetch(`${API_BASE}/call-center/module-info/${SANTRAL_MODULE_ID}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        cache: "no-store",
      });
      const json = await response.json().catch(() => ({}));
      if (response.ok && json?.success !== false) setModuleInfo(json.data || null);
    } catch {
      setModuleInfo(null);
    }
  }, []);

  useEffect(() => {
    if (!inactiveInfoOpen || moduleInfo) return;
    openUnavailableInfo();
  }, [inactiveInfoOpen, moduleInfo, openUnavailableInfo]);

  const openManualCallDialer = useCallback(() => {
    if (eligibility.loading) {
      showToast("Santral yetkisi kontrol ediliyor.", "info", 1800);
      return true;
    }

    if (!eligibility.santralActive) {
      openUnavailableInfo();
      return true;
    }

    if (eligibility.canDial) return false;

    setCallbackUserPhone(normalizeTelsamPhone(user?.phone || ""));
    setCallbackCustomerPhone("");
    setCallbackCustomerName("");
    setCallbackError("");
    setCallbackOpen(true);
    return true;
  }, [eligibility.canDial, eligibility.loading, eligibility.santralActive, openUnavailableInfo, showToast, user?.phone]);

  const startCallbackCall = useCallback(async () => {
    const userPhone = normalizeTelsamPhone(callbackUserPhone);
    const customerPhone = normalizeTelsamPhone(callbackCustomerPhone);
    if (!userPhone || userPhone.length !== 12) {
      setCallbackError("Kullanıcı telefon numarası geçerli değil.");
      return;
    }
    if (!customerPhone || customerPhone.length !== 12) {
      setCallbackError("Müşteri telefon numarası geçerli değil.");
      return;
    }

    setCallbackSaving(true);
    setCallbackError("");
    try {
      const response = await fetch(`${API_BASE}/call-center/callback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ userPhone, customerPhone }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.success === false) throw new Error(json?.message || "Çağrı başlatılamadı");
      showToast(json?.message || "Çağrı başlatıldı.", "success", 3500);
      setCallbackOpen(false);
    } catch (error) {
      setCallbackError(error instanceof Error ? error.message : "Çağrı başlatılamadı");
    } finally {
      setCallbackSaving(false);
    }
  }, [callbackCustomerPhone, callbackUserPhone, showToast]);

  const submitInfoRequest = useCallback(async () => {
    if (infoRequestSaving) return;
    setInfoRequestSaving(true);
    setInfoRequestMessage("");
    try {
      const response = await fetch(`${API_BASE}/call-center/module-info/${SANTRAL_MODULE_ID}/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          source: "santral_inactive_modal",
          path: typeof window !== "undefined" ? window.location.pathname : "",
          metadata: {
            module: "mod_353",
            title: "Santral ve Çağrı Merkezi",
          },
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json?.success === false) throw new Error(json?.message || "Bilgi talebi alınamadı");
      setInfoRequestMessage(json?.message || "Bilgi talebiniz alındı.");
      showToast(json?.message || "Bilgi talebiniz alındı.", "success", 3000);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Bilgi talebi alınamadı";
      setInfoRequestMessage(message);
      showToast(message, "error", 3000);
    } finally {
      setInfoRequestSaving(false);
    }
  }, [infoRequestSaving, showToast]);

  const callModal = useMemo(() => {
    if (inactiveInfoOpen) {
      const title = moduleInfo?.title || "Santral ve Çağrı Merkezi";
      const introTitle = moduleInfo?.introTitle || "Panel içinden arama deneyimi";
      const introText = moduleInfo?.introText || "Santral modülü aktif olduğunda personeller panelden müşteri araması başlatabilir, çağrı merkezi kullanıcıları web softphone ile görüşme yapabilir.";
      const features = moduleInfo?.features?.length ? moduleInfo.features : [
        { title: "Yetkiye göre akış", text: "Çağrı merkezi personeli softphone kullanır, diğer personeller telefonla birleştirme akışıyla arama başlatır." },
        { title: "Telsam köprüsü", text: "Personel telefonu ile aranacak numara santral üzerinden sıralı aranır ve görüşme birleştirilir." },
        { title: "Kayıt ve izleme", text: "Arama geçmişi, çağrı durumu ve müşteri eşleşmeleri panel içinde takip edilebilir." },
      ];
      const hasVideo = Boolean(moduleInfo?.videoUrl);
      const hasFaqs = Boolean(moduleInfo?.faqs?.length);
      const modal = (
        <div className="customer-call-service" role="dialog" aria-modal="true" aria-label="Santral Hizmeti">
          <div className="customer-call-service__panel">
            <div className="customer-call-service__header">
              <div>
                <span>Farktor Telefon</span>
                <strong>{title}</strong>
              </div>
              <button type="button" onClick={() => setInactiveInfoOpen(false)} aria-label="Kapat">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="customer-call-service__hero">
              <div className="customer-call-service__icon">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <div>
                <strong>{introTitle}</strong>
                <p>{introText}</p>
              </div>
            </div>

            <div className="customer-call-service__grid">
              {features.slice(0, 3).map((feature, index) => (
                <div key={`${feature.title}-${index}`}>
                  <FontAwesomeIcon icon={index === 0 ? faUsers : index === 1 ? faRoute : faCircleInfo} />
                  <strong>{feature.title}</strong>
                  <span>{feature.text}</span>
                </div>
              ))}
            </div>

            {(hasVideo || hasFaqs) && (
              <div className="customer-call-service__links">
                {hasVideo && (
                  <button type="button" onClick={() => setVideoOpen(true)}>Videoyu İzle</button>
                )}
                {hasFaqs && (
                  <button type="button" onClick={() => setFaqOpen(true)}>Sık Sorulan Sorular</button>
                )}
              </div>
            )}

            <div className="customer-call-service__footer">
              <span>Bu hizmet mağazanızda bulunmamaktadır. Sistem yöneticiniz ile iletişime geçebilirsiniz.</span>
              <button type="button" onClick={submitInfoRequest} disabled={infoRequestSaving}>
                {infoRequestSaving ? "Gönderiliyor" : "Detaylı Bilgi Talep Et"}
              </button>
            </div>
            {infoRequestMessage && (
              <div className="customer-call-service__feedback">{infoRequestMessage}</div>
            )}
          </div>
          {videoOpen && moduleInfo?.videoUrl && (
            <div className="customer-call-service__submodal" role="dialog" aria-modal="true">
              <div className="customer-call-service__subpanel">
                <div className="customer-call-service__subheader">
                  <strong>Video</strong>
                  <button type="button" onClick={() => setVideoOpen(false)}><FontAwesomeIcon icon={faXmark} /></button>
                </div>
                <iframe src={moduleInfo.videoUrl} title={`${title} video`} allow="autoplay; fullscreen; picture-in-picture" />
              </div>
            </div>
          )}
          {faqOpen && !!moduleInfo?.faqs?.length && (
            <div className="customer-call-service__submodal" role="dialog" aria-modal="true">
              <div className="customer-call-service__subpanel">
                <div className="customer-call-service__subheader">
                  <strong>Sık Sorulan Sorular</strong>
                  <button type="button" onClick={() => setFaqOpen(false)}><FontAwesomeIcon icon={faXmark} /></button>
                </div>
                <div className="customer-call-service__faq">
                  {moduleInfo.faqs.map((faq, index) => (
                    <details key={`${faq.question}-${index}`}>
                      <summary>{faq.question}</summary>
                      <p>{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      );
      return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
    }

    if (!callbackOpen) return null;
    const normalizedUserPhone = normalizeTelsamPhone(callbackUserPhone);
    const normalizedCustomerPhone = normalizeTelsamPhone(callbackCustomerPhone);
    const canStart =
      !callbackSaving &&
      normalizedUserPhone.length === 12 &&
      normalizedCustomerPhone.length === 12;

    const modal = (
      <div className="softphone softphone--callback">
        <div className="softphone__panel customer-call-phone" role="dialog" aria-label="Telefonla Birleştir">
          <div className="softphone__header">
            <div>
              <span className="softphone__eyebrow">Farktor Telefon</span>
              <strong>Telefonla Birleştir</strong>
            </div>
            <button type="button" onClick={() => setCallbackOpen(false)} aria-label="Kapat">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className="softphone__status softphone__status--registered customer-call-phone__status">
            <FontAwesomeIcon icon={faCircle} />
            <span>Telsam köprüsü hazır</span>
            <small>İkili arama</small>
          </div>

          <div className="customer-call-phone__notice">
            <FontAwesomeIcon icon={faCircleInfo} />
            <span>
              {callbackCustomerName
                ? "Çağrı başlatıldığında önce personel telefonu aranır. Personel açınca müşteri aranır ve görüşme telefon üzerinden birleştirilir."
                : "Çağrı başlatıldığında önce personel telefonu aranır. Personel açınca girilen numara aranır ve görüşme telefon üzerinden birleştirilir."}
            </span>
          </div>

          {callbackCustomerName && (
            <div className="customer-call-phone__customer">
              <span>Müşteri</span>
              <strong>{callbackCustomerName}</strong>
            </div>
          )}

          <label className="softphone__field customer-call-phone__field">
            <span>Personel Telefonu</span>
            <input
              value={callbackUserPhone}
              onChange={(event) => setCallbackUserPhone(event.target.value)}
              placeholder="05xx veya 5xx"
              inputMode="tel"
            />
          </label>

          <label className="softphone__field customer-call-phone__field">
            <span>{callbackCustomerName ? "Müşteri Telefonu" : "Aranacak Telefon"}</span>
            <input
              value={callbackCustomerPhone}
              onChange={(event) => setCallbackCustomerPhone(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && canStart) startCallbackCall();
              }}
              placeholder="05xx veya 5xx"
              inputMode="tel"
            />
          </label>

          {callbackError && <div className="softphone__message">{callbackError}</div>}

          <div className="softphone__actions">
            <button
              type="button"
              className="softphone__call"
              onClick={startCallbackCall}
              disabled={!canStart}
            >
              <FontAwesomeIcon icon={faPhone} />
              {callbackSaving ? "Başlatılıyor" : "Çağrı Başlat"}
            </button>
            <button
              type="button"
              className="softphone__hangup"
              onClick={() => setCallbackOpen(false)}
              disabled={callbackSaving}
              aria-label="Kapat"
            >
              <FontAwesomeIcon icon={faPhoneSlash} />
            </button>
          </div>
        </div>
      </div>
    );
    return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
  }, [
    callbackCustomerName,
    callbackCustomerPhone,
    callbackError,
    callbackOpen,
    callbackSaving,
    callbackUserPhone,
    inactiveInfoOpen,
    infoRequestMessage,
    infoRequestSaving,
    moduleInfo,
    faqOpen,
    videoOpen,
    startCallbackCall,
    submitInfoRequest,
  ]);

  return {
    openCustomerCall,
    openManualCallDialer,
    callModal,
    eligibility,
  };
}
